<template>
  <el-dialog
    class="client-form-dialog"
    :close-on-click-modal="false"
    close-on-press-escape
    :model-value="visible"
    @close="$emit('close')"
    width="760px"
    destroy-on-close
  >
    <template #header>
      <div class="client-form-dialog__header">
        <div v-if="form.id" class="client-form-summary">
          <div class="client-form-summary__name">{{ form.fio || 'Клиент' }}</div>
          <div
            v-if="form.balance !== undefined"
            :class="['client-balance', { negative: form.balance < 0, positive: form.balance > 0 }]"
          >
            Баланс: <b>{{ Number(form.balance).toLocaleString() }} ₸</b>
          </div>
        </div>
      </div>
    </template>

    <el-tabs v-model="activeTab" class="client-form-tabs">
      <el-tab-pane label="Клиент" name="client" class="client-form-pane">
        <el-form
          :model="form"
          ref="formRef"
          class="client-form-layout"
          label-width="120px"
          label-position="top"
          hide-required-asterisk
          size="large"
        >
          <div class="client-form-top-card">
            <div class="client-form-top-card__avatar">
              <el-avatar
                class="avatar"
                :src="avatarSrc"
                :size="82"
                shape="circle"
                @click="onAvatarClick"
              >
                {{ avatarInitials }}
              </el-avatar>
            </div>

            <div class="client-form-top-card__content">
              <div class="client-form-top-grid">
                <el-form-item class="client-field" prop="fio" required>
                  <template #label>
                    <span class="client-required-label">
                      <span class="client-required-mark">*</span>
                      <span>ФИО</span>
                    </span>
                  </template>
                  <el-input v-model="form.fio" autocomplete="off" />
                </el-form-item>

                <el-form-item class="client-field" label="Дата рождения" prop="birth_date">
                  <el-date-picker v-model="form.birth_date" type="date" value-format="YYYY-MM-DD" />
                </el-form-item>

                <el-form-item class="client-field client-field--nickname" label="Никнейм" prop="telegram_user_id">
                  <el-input v-model="form.telegram_user_id" placeholder="@nickname" />
                </el-form-item>

                <el-form-item class="client-field client-field--sex" label="Пол" prop="sex">
                  <el-radio-group v-model="form.sex" class="client-sex-group">
                    <el-radio-button :label="1">муж</el-radio-button>
                    <el-radio-button :label="2">жен</el-radio-button>
                  </el-radio-group>
                </el-form-item>
              </div>
            </div>
          </div>

          <section class="client-form-section">
            <div class="client-form-section__title">Контактная информация</div>
            <div class="client-form-section__grid client-form-section__grid--three">
              <el-form-item
                class="client-field client-field--phone"
                label="Телефон"
                prop="phone"
                :rules="[{ validator: atLeastOneContact, trigger: 'blur' }]"
              >
                <vue-tel-input
                  v-model="form.phone"
                  @validate="handlePhoneValidation"
                  :format-on-display="true"
                  :mode="'international'"
                  v-bind="bindProps"
                  :preferred-countries="['ru', 'by', 'kz']"
                />
              </el-form-item>

              <el-form-item
                class="client-field"
                label="Электронная почта"
                prop="email"
                :rules="[{ validator: atLeastOneContact, trigger: 'blur' }]"
              >
                <el-input v-model="form.email" type="email" placeholder="Введите e-mail" />
              </el-form-item>

              <el-form-item
                class="client-field"
                label="Telegram ID / Имя пользователя"
                prop="telegram_user_id"
                :rules="[{ validator: atLeastOneContact, trigger: 'blur' }]"
              >
                <el-input v-model="form.telegram_user_id" placeholder="Telegram ID или имя пользователя" />
              </el-form-item>
            </div>
          </section>

          <section class="client-form-section">
            <div class="client-form-section__title">Адрес и локация</div>
            <div class="client-form-section__grid client-form-section__grid--two">
              <el-form-item class="client-field" label="Город" prop="city_id">
                <el-select v-model="form.city_id" filterable placeholder="Город">
                  <el-option v-for="city in cities" :key="city.id" :label="city.name" :value="city.id" />
                </el-select>
              </el-form-item>

              <el-form-item class="client-field" label="Адрес" prop="address">
                <el-input v-model="form.address" />
              </el-form-item>
            </div>
          </section>

          <section class="client-form-section">
            <div class="client-form-section__title">Дополнительно</div>
            <div class="client-form-section__grid client-form-section__grid--two">
              <el-form-item class="client-field" label="ИИН" prop="inn">
                <el-input v-model="form.inn" maxlength="24" />
              </el-form-item>

              <el-form-item class="client-field" label="Документ" prop="document">
                <el-input v-model="form.document" maxlength="64" />
              </el-form-item>

              <el-form-item class="client-field client-field--comment" label="Комментарий" prop="comment">
                <el-input
                  v-model="form.comment"
                  type="textarea"
                  :rows="3"
                  placeholder="Комментарий о клиенте"
                />
              </el-form-item>
            </div>
          </section>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="Посещения" name="appointments" :disabled="!form.id">
        <div class="client-form-tab-placeholder">В разработке...</div>
      </el-tab-pane>

      <el-tab-pane label="Абонементы" name="subscriptions" :disabled="!form.id">
        <div class="client-form-tab-panel">
          <ClientAbonements
            v-if="form.id"
            :client-id="form.id"
            @abonement-updated="fetchClientData"
          />
        </div>
      </el-tab-pane>

      <el-tab-pane label="Операции" name="operations" :disabled="!form.id">
        <div class="client-form-tab-panel">
          <ClientPayments v-if="form.id" :client-id="form.id" @abonement-updated="fetchClientData" />
        </div>
      </el-tab-pane>
    </el-tabs>

    <template #footer>
      <el-button @click="$emit('close')">Отмена</el-button>
      <el-button
        type="primary"
        @click="onSave"
        :loading="saving"
        :disabled="saving"
      >
        {{ form.id ? 'Обновить' : 'Создать' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { mapActions, mapState } from 'vuex'
import ClientAbonements from '@/components/util/clients/ClientAbonements.vue'
import ClientPayments from '@/components/util/clients/ClientPayments.vue'

export default {
  name: 'ClientForm',
  components: {
    ClientPayments,
    ClientAbonements
  },
  props: {
    visible: Boolean,
    model: Object,
    cities: Array
  },
  data() {
    return {
      saving: false,
      isPhoneComplete: false,
      bindProps: {
        inputOptions: {
          placeholder: 'Введите номер'
        }
      },
      customFieldValues: {},
      form: { ...(this.model || {}) },
      rules: {
        fio: [{ required: true, message: 'Укажите ФИО', trigger: 'blur' }],
        phone: [{ required: true, message: 'Телефон обязателен', trigger: 'blur' }],
        email: [
          { type: 'email', message: 'Введите корректный email', trigger: ['blur', 'change'] }
        ]
      },
      activeTab: 'client',
      duplicateClient: null,
      duplicateEmail: null,
      checkingDuplicate: false,
      initialized: false,
      initialPhone: '',
      loadingInit: false
    }
  },
  computed: {
    ...mapState('clients', ['search_clients']),
    ...mapState('custom_fields', ['fields']),
    customFields() {
      return this.fields
    },
    customRules() {
      const rules = {}
      for (const field of this.customFields) {
        if (field.required && field.type === 'number') {
          rules[`custom_${field.id}`] = [
            { required: true, message: 'Обязательное поле', trigger: 'change' },
            {
              validator: (rule, value, callback) => {
                if (typeof value !== 'number' || isNaN(value)) {
                  callback(new Error('Обязательное поле'))
                } else {
                  callback()
                }
              },
              trigger: 'change'
            }
          ]
        } else if (field.required) {
          rules[`custom_${field.id}`] = [{ required: true, message: 'Обязательное поле', trigger: 'blur' }]
        }
      }
      return rules
    },
    avatarSrc() {
      if (this.form.avatar) return this.form.avatar
      if (this.form.sex === 1) return '/img/avatar-male.svg'
      if (this.form.sex === 2) return '/img/avatar-female.svg'
      if (this.form.fio) return null
      return '/img/avatar-male.svg'
    },
    avatarInitials() {
      if (!this.form.fio) return ''
      const parts = this.form.fio.trim().split(/\s+/)
      return parts.slice(0, 2).map(part => part[0]?.toUpperCase() || '').join('') || ''
    }
  },
  watch: {
    visible(val) {
      if (val) this.activeTab = 'client'
    },
    model: {
      handler(val) {
        const nextModel = val || {}
        this.form = { ...nextModel }
        this.initialPhone = nextModel.phone ?? ''
        this.initialized = true
        this.customFieldValues = {}

        if (nextModel.custom_fields) {
          for (const [id, value] of Object.entries(nextModel.custom_fields)) {
            const field = this.customFields.find(item => item.id === id)
            if (field && field.type === 'number') {
              this.customFieldValues[id] = value !== null && value !== undefined && value !== '' ? Number(value) : null
            } else if (field && (field.type === 'date' || field.type === 'datetime')) {
              this.customFieldValues[id] = typeof value === 'string' && value.length ? value : null
            } else {
              this.customFieldValues[id] = value
            }
          }
        }

        this.isPhoneComplete = false
        this.loadingInit = true

        this.$nextTick(() => {
          const oldPhone = this.form.phone
          this.form.phone = ''
          this.$nextTick(() => {
            this.form.phone = oldPhone
            this.loadingInit = false
          })
        })
      },
      deep: true,
      immediate: true
    },
    customFields: {
      handler() {
        this.rules = {
          fio: [{ required: true, message: 'Укажите ФИО', trigger: 'blur' }],
          phone: [{ required: true, message: 'Телефон обязателен', trigger: 'blur' }],
          email: [
            { type: 'email', message: 'Введите корректный email', trigger: ['blur', 'change'] }
          ],
          ...this.customRules
        }
      },
      immediate: true
    },
    'form.phone': {
      async handler(newVal) {
        if (!this.initialized || this.loadingInit || newVal === this.initialPhone) return

        const cleaned = typeof newVal === 'string' ? newVal.replace(/\D/g, '') : ''
        if (cleaned.length >= 5) {
          this.checkingDuplicate = true
          try {
            await this.searchClients({ query: cleaned })
            const found = (this.search_clients || []).find(client => {
              const clientPhone = (client.phone || '').replace(/\D/g, '')
              return clientPhone.endsWith(cleaned) && client.id !== this.form.id
            })
            this.duplicateClient = found || null
          } catch (error) {
            console.error(error)
          } finally {
            this.checkingDuplicate = false
          }
        } else {
          this.duplicateClient = null
        }
      }
    },
    'form.email': {
      async handler(newVal) {
        if (!this.initialized || this.loadingInit) return

        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!newVal || !emailPattern.test(newVal.trim())) {
          this.duplicateEmail = null
          return
        }

        const query = newVal.trim().toLowerCase()
        try {
          await this.searchClients({ query })
          const found = (this.search_clients || []).find(client =>
            (client.email || '').toLowerCase() === query && client.id !== this.form.id
          )
          this.duplicateEmail = found || null
        } catch (error) {
          console.error(error)
        }
      }
    }
  },
  methods: {
    ...mapActions('clients', ['searchClients']),
    ...mapActions('payments', ['fetchPayments']),
    onAvatarClick() {
      console.log('avatar click')
    },
    atLeastOneContact(rule, value, callback) {
      const phone = (this.form?.phone || '').trim()
      const email = (this.form?.email || '').trim()
      const telegram = (this.form?.telegram_user_id || '').trim()
      if (phone || email || telegram) return callback()
      callback(new Error('Укажите хотя бы один способ связи'))
    },
    handlePhoneValidation(isValid) {
      this.isPhoneComplete = isValid.valid
    },
    async onSave() {
      if (this.saving) return
      this.saving = true

      try {
        Object.entries(this.customFieldValues).forEach(([id, val]) => {
          const field = this.customFields.find(item => item.id === id)
          if (field && field.type === 'number' && typeof val !== 'number' && val !== null && val !== '') {
            this.customFieldValues[id] = Number(val)
          }
        })

        if (this.duplicateClient) {
          ElMessage.error(`Клиент с таким номером уже существует: ${this.duplicateClient.fio}`)
          this.saving = false
          return
        }

        if (this.duplicateEmail) {
          ElMessage.error(`Клиент с таким email уже существует: ${this.duplicateEmail.fio}`)
          this.saving = false
          return
        }

        this.$refs.formRef.validate(async valid => {
          if (!valid) {
            this.saving = false
            return
          }

          if (this.form.phone && !this.isPhoneComplete) {
            ElMessage.error('Пожалуйста, введите полный номер телефона')
            this.saving = false
            return
          }

          const formWithCustom = {
            ...this.form,
            custom_fields: { ...this.customFieldValues }
          }

          try {
            const savedClient = await new Promise(resolve => {
              this.$emit('save', formWithCustom, resolve)
            })

            if (savedClient && savedClient.id) {
              this.form = { ...savedClient }
              this.initialPhone = savedClient.phone ?? ''
              this.activeTab = 'client'
              ElMessage.success('Клиент сохранен')
              this.$nextTick(() => {
                this.form.id = savedClient.id
              })
            }
          } catch (error) {
            console.error(error)
            ElMessage.error('Ошибка при сохранении клиента')
          } finally {
            this.saving = false
          }
        })
      } catch (error) {
        console.error(error)
        this.saving = false
      }
    },
    async fetchClientData() {
      try {
        const response = await this.$store.dispatch('clients/getClientById', { id: this.form.id })
        if (response && response.client) {
          this.form = { ...response.client }
          ElMessage.success('Данные клиента обновлены')
        }
      } catch (error) {
        console.error('Ошибка при обновлении клиента:', error)
        ElMessage.error('Не удалось обновить данные клиента')
      }
      await this.fetchPayments({ client_id: this.form.id })
    }
  }
}
</script>

<style scoped>
.avatar {
  font-size: 28px;
}

.client-form-dialog__header {
  width: 100%;
}

.client-form-summary {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 16px;
  background: linear-gradient(97deg, rgba(17, 40, 84, 0.98) 0%, rgba(12, 29, 64, 0.96) 100%);
  border-radius: 8px;
}

.client-form-summary__name {
  color: var(--cal-text-inverse);
  font-size: 18px;
  font-weight: 500;
  text-align: left;
}

.client-balance {
  font-size: 14px;
  font-weight: 500;
  color: var(--cal-text-inverse);
}

.client-balance.positive {
  color: #8de5a5;
}

.client-balance.negative {
  color: #ff9a9a;
}

.client-form-layout {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.client-form-top-card {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}

.client-form-top-card__avatar {
  display: flex;
  justify-content: center;
  padding-top: 8px;
}

.client-form-top-card__content {
  min-width: 0;
}

.client-form-top-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 1.2fr);
  gap: 10px;
  align-items: start;
}

.client-field {
  margin-bottom: 0;
}

.client-field--sex :deep(.el-form-item__content) {
  display: flex;
  align-items: stretch;
}

.client-required-label {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.client-required-mark {
  color: #ffffff;
}

.client-field--comment {
  grid-column: 1 / -1;
}

.client-form-section {
  padding: 10px 8px 12px;
  background:
    linear-gradient(rgba(16, 53, 117, 0.49), rgba(16, 53, 117, 0.49)),
    linear-gradient(299deg, #030B1B 13.22%, #15264E 86.78%);
  border-radius: 8px;
}

.client-form-section__title {
  margin-bottom: 10px;
  text-align: center;
  color: #00c3fe;
  font-size: 17px;
  font-weight: 500;
}

.client-form-section__grid {
  display: grid;
  gap: 10px;
  min-width: 0;
}

.client-form-section__grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.client-form-section__grid--two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.client-form-tab-placeholder,
.client-form-tab-panel {
  padding: 16px 0 4px;
  color: var(--ui-color-text-muted);
}

.client-form-pane {
  min-width: 0;
}

.client-sex-group {
  display: flex;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  align-items: stretch;
}

.client-sex-group :deep(.el-radio-button) {
  flex: 1 1 0;
  display: flex;
}

.client-sex-group :deep(.el-radio-button__inner) {
  width: 100%;
  height: 40px !important;
}

@media (max-width: 768px) {
  .client-form-top-card {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .client-form-top-card__avatar {
    padding-top: 0;
    justify-content: center;
  }

  .client-form-top-grid,
  .client-form-section__grid--three,
  .client-form-section__grid--two {
    grid-template-columns: minmax(0, 1fr);
  }

  .client-form-top-grid {
    gap: 10px;
  }

  .client-form-section {
    padding: 10px 10px 12px;
  }

  .client-form-section__title {
    margin-bottom: 8px;
    font-size: 15px;
  }

  .client-field--sex,
  .client-field--nickname,
  .client-field--comment {
    grid-column: auto;
  }
}

@media (max-width: 420px) {
  .client-form-top-card {
    gap: 12px;
  }

  .client-form-top-grid {
    gap: 8px;
  }
}
</style>

<style>
.client-form-dialog {
  --el-dialog-bg-color: #18366c;
}

.client-form-dialog.el-dialog {
  width: min(760px, calc(100vw - 32px)) !important;
  max-width: calc(100vw - 32px) !important;
  margin-top: 3vh !important;
  background-color: #334878 !important;
  background:
    linear-gradient(rgba(0, 108, 254, 0.22), rgba(0, 108, 254, 0.22)),
    linear-gradient(299deg, #030B1B 13.22%, #15264E 86.78%) !important;
  background-image:
    linear-gradient(rgba(0, 108, 254, 0.22), rgba(0, 108, 254, 0.22)),
    linear-gradient(299deg, #030B1B 13.22%, #15264E 86.78%) !important;
  border: 1px solid rgba(91, 130, 202, 0.4) !important;
  box-shadow: 0 18px 34px rgba(2, 10, 28, 0.35) !important;
  border-radius: 10px !important;
  overflow: hidden !important;
}

.client-form-dialog .el-dialog__header,
.client-form-dialog .el-dialog__body,
.client-form-dialog .el-dialog__footer {
  background: transparent;
}

.client-form-dialog .el-dialog__header {
  padding: 10px 16px 0;
}

.client-form-dialog .el-dialog__body {
  padding: 6px 16px 14px;
  max-height: calc(100vh - 112px);
  overflow-y: auto;
  overflow-x: hidden;
}

.client-form-dialog .el-dialog__footer {
  padding: 6px 16px 14px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.client-form-dialog .el-dialog__headerbtn {
  top: 14px;
  right: 16px;
}

.client-form-dialog .el-dialog__close {
  color: rgba(10, 16, 30, 0.92) !important;
  font-size: 18px !important;
}

.client-form-dialog .el-tabs__header {
  margin-bottom: 10px;
}

.client-form-dialog .el-tabs__nav-wrap::after {
  background: transparent;
}

.client-form-dialog .el-tabs__item {
  padding: 0 18px 0 0 !important;
  color: #d4e3ff !important;
  font-family: "Oswald", sans-serif !important;
  font-size: 14px !important;
}

.client-form-dialog .el-tabs__item.is-active,
.client-form-dialog .el-tabs__item:hover {
  color: #00c3fe !important;
}

.client-form-dialog .el-tabs__active-bar {
  background: #00c3fe !important;
  height: 1px !important;
}

.client-form-dialog .el-form-item__label {
  color: #ffffff !important;
  font-family: "Oswald", sans-serif !important;
  font-size: 13px !important;
  line-height: 1.1 !important;
  padding-bottom: 6px !important;
}

.client-form-dialog .el-input__wrapper,
.client-form-dialog .el-input .el-input__wrapper,
.client-form-dialog .el-select__wrapper,
.client-form-dialog .el-select .el-select__wrapper,
.client-form-dialog .el-date-editor .el-input__wrapper,
.client-form-dialog .el-date-editor.el-input .el-input__wrapper,
.client-form-dialog .el-textarea__inner {
  background-color: #254c90 !important;
  background-image: none !important;
  box-shadow: inset 0 0 0 1px rgba(0, 195, 254, 0.28) !important;
  border: none !important;
  border-radius: 6px !important;
}

.client-form-dialog .el-input__wrapper:hover,
.client-form-dialog .el-input__wrapper.is-focus,
.client-form-dialog .el-input.is-focus .el-input__wrapper,
.client-form-dialog .el-select__wrapper:hover,
.client-form-dialog .el-select__wrapper.is-focused,
.client-form-dialog .el-date-editor .el-input__wrapper:hover,
.client-form-dialog .el-date-editor .el-input__wrapper.is-focus,
.client-form-dialog .el-textarea__inner:hover,
.client-form-dialog .el-textarea__inner:focus {
  background-color: #254c90 !important;
  background-image: none !important;
  box-shadow: inset 0 0 0 1px #00c3fe !important;
}

.client-form-dialog .el-input__inner,
.client-form-dialog .el-input__inner:not([type="date"]),
.client-form-dialog .el-input__inner::placeholder,
.client-form-dialog .el-select__placeholder.is-transparent,
.client-form-dialog .el-select__placeholder,
.client-form-dialog .el-select__selected-item,
.client-form-dialog .el-select__selected-item span,
.client-form-dialog .el-textarea__inner,
.client-form-dialog .el-textarea__inner::placeholder,
.client-form-dialog .el-range-input {
  color: #00c3fe !important;
  -webkit-text-fill-color: #00c3fe !important;
  font-family: "Oswald", sans-serif !important;
  font-weight: 300 !important;
  font-size: 13px !important;
  background-color: transparent !important;
}

.client-form-dialog .el-textarea__inner {
  min-height: 56px !important;
  resize: vertical;
}

.client-form-dialog .el-dialog__footer .el-button {
  min-width: 120px;
  min-height: 38px;
}

.client-form-dialog .el-button--default {
  background: transparent !important;
  border: none !important;
  box-shadow: 0 0 0 1px rgba(180, 214, 255, 0.45) inset !important;
  color: #ffffff !important;
}

.client-form-dialog .el-button--default:hover,
.client-form-dialog .el-button--default:focus,
.client-form-dialog .el-button--default:active {
  background: transparent !important;
  color: #ffffff !important;
  box-shadow: 0 0 0 1px rgba(180, 214, 255, 0.72) inset !important;
}

.client-form-dialog .el-button--primary {
  background: #00c3fe !important;
  border: none !important;
  color: #ffffff !important;
}

.client-form-dialog .el-radio-button {
  border: none !important;
}

.client-form-dialog .el-radio-button__inner {
  min-width: 60px;
  height: 40px !important;
  padding: 0 14px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #254c90 !important;
  border: none !important;
  box-shadow: inset 0 0 0 1px rgba(0, 195, 254, 0.28) !important;
  color: #00c3fe !important;
  border-radius: 6px !important;
  font-weight: 300 !important;
  width: 100% !important;
  line-height: 1 !important;
  vertical-align: top !important;
}

.client-form-dialog .el-radio-button__inner span {
  color: #00c3fe !important;
  font-weight: 300 !important;
}

.client-form-dialog .el-radio-button__original-radio + .el-radio-button__inner,
.client-form-dialog .el-radio-button__original-radio + .el-radio-button__inner * {
  color: #00c3fe !important;
}

.client-form-dialog .el-radio-button__inner:hover {
  color: #00c3fe !important;
  box-shadow: inset 0 0 0 1px #00c3fe !important;
}

.client-form-dialog .el-radio-button__original-radio:checked + .el-radio-button__inner {
  background: #00c3fe !important;
  color: #ffffff !important;
  box-shadow: none !important;
}

.client-form-dialog .el-radio-button__original-radio:checked + .el-radio-button__inner span {
  color: #ffffff !important;
}

.client-form-dialog .vue-tel-input .vti__dropdown {
  z-index: 3000 !important;
}

.client-form-dialog .vue-tel-input {
  height: 40px !important;
  border: none !important;
  width: 100% !important;
  display: flex !important;
  align-items: stretch !important;
  gap: 10px !important;
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.client-form-dialog .vue-tel-input .vti__input,
.client-form-dialog .vue-tel-input .vti__input::placeholder {
  color: #00c3fe !important;
  background: #254c90 !important;
  font-family: "Oswald", sans-serif !important;
  font-weight: 300 !important;
}

.client-form-dialog .vue-tel-input .vti__input {
  border-radius: 6px !important;
  box-shadow: inset 0 0 0 1px rgba(0, 195, 254, 0.28) !important;
  padding-left: 12px !important;
  margin-left: 0 !important;
  flex: 1 1 auto !important;
  border: none !important;
}

.client-form-dialog .vue-tel-input .vti__input:hover,
.client-form-dialog .vue-tel-input .vti__input:focus {
  box-shadow: inset 0 0 0 1px #00c3fe !important;
  outline: none !important;
}

.client-form-dialog .vue-tel-input .vti__dropdown {
  flex: 0 0 40px !important;
  width: 44px !important;
  justify-content: center !important;
  background: transparent !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  margin-right: 0 !important;
  border-right: none !important;
  border: none !important;
  padding: 0 !important;
}

.client-form-dialog .vue-tel-input .vti__dropdown:hover {
  box-shadow: none !important;
}

.client-form-dialog .vue-tel-input .vti__dropdown,
.client-form-dialog .vue-tel-input .vti__dropdown-arrow,
.client-form-dialog .vue-tel-input .vti__dropdown-item {
  color: #00c3fe !important;
}

.client-form-dialog .vue-tel-input .vti__selection,
.client-form-dialog .vue-tel-input .vti__dropdown-arrow {
  background: transparent !important;
}

.client-form-dialog .vue-tel-input .vti__selection {
  padding: 0 !important;
  justify-content: center !important;
  width: 100% !important;
}

.client-form-dialog .vue-tel-input .vti__dropdown,
.client-form-dialog .vue-tel-input .vti__input {
  height: 40px !important;
}

.client-form-dialog .vue-tel-input .vti__dropdown-list {
  background: #18366c !important;
}

.client-form-dialog .vue-tel-input .vti__dropdown-item {
  color: #ffffff !important;
}

.client-form-dialog .client-field--phone .el-form-item__content {
  background: transparent !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  padding: 0 !important;
  min-height: 40px !important;
}

.client-form-dialog .client-field--phone .vue-tel-input .vti__input,
.client-form-dialog .client-field--phone .vue-tel-input .vti__selection {
  background-color: transparent !important;
}

.client-form-dialog .client-field--phone .vue-tel-input .vti__dropdown {
  border-radius: 0 !important;
}

.client-form-dialog .client-field--phone .vue-tel-input .vti__input {
  border-radius: 6px !important;
  background-color: #254c90 !important;
  background-image: none !important;
  height: 40px !important;
  line-height: 40px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  margin-left: 0 !important;
}

.client-form-dialog .client-field--phone .vue-tel-input .vti__flag {
  transform: scale(1.28);
  transform-origin: center;
}

.client-form-dialog .client-field--phone .vue-tel-input .vti__dropdown-arrow {
  margin-left: 2px !important;
}

.client-form-dialog .vue-tel-input-block {
  height: 40px !important;
  justify-content: flex-start;
  padding: 2px;
  z-index: 99999999;
}

.client-form-dialog .el-date-editor {
  width: 100%;
}

.client-form-dialog .el-date-editor .el-input__prefix,
.client-form-dialog .el-date-editor .el-input__suffix,
.client-form-dialog .el-select__caret {
  color: #00c3fe !important;
}

.client-form-dialog .el-date-editor .el-input__prefix,
.client-form-dialog .el-date-editor .el-input__prefix svg,
.client-form-dialog .el-date-editor .el-input__prefix svg path {
  color: #000000 !important;
  fill: #000000 !important;
}

.client-form-dialog .avatar {
  background: #d9e1ec !important;
  color: #51627b !important;
  box-shadow: 0 0 0 4px rgba(17, 36, 76, 0.18);
}

@media (max-width: 768px) {
  .client-form-dialog.el-dialog {
    width: calc(100vw - 14px) !important;
    max-width: calc(100vw - 14px) !important;
    margin: 7px auto 0 !important;
  }

  .client-form-dialog .el-dialog__header {
    padding: 10px 12px 0;
  }

  .client-form-dialog .el-dialog__body {
    padding: 8px 12px 12px;
    max-height: calc(100vh - 96px);
  }

  .client-form-dialog .el-dialog__footer {
    padding: 8px 12px 12px;
    gap: 8px;
  }

  .client-form-dialog .el-tabs__header {
    margin-bottom: 8px;
  }

  .client-form-dialog .el-tabs__nav-wrap {
    overflow-x: auto !important;
  }

  .client-form-dialog .el-tabs__item {
    font-size: 12px !important;
    padding-right: 12px !important;
  }

  .client-form-summary {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 10px 12px;
  }

  .client-form-top-card {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .client-form-top-card__avatar {
    padding-top: 0;
  }

  .client-form-top-grid,
  .client-form-section__grid--three,
  .client-form-section__grid--two {
    grid-template-columns: 1fr;
  }

  .client-field--sex,
  .client-field--comment {
    grid-column: auto;
  }

  .client-form-section {
    padding: 10px 10px 12px;
    overflow: hidden;
  }

  .client-form-section__title {
    margin-bottom: 8px;
    font-size: 15px;
  }

  .client-form-dialog .el-input,
  .client-form-dialog .el-select,
  .client-form-dialog .el-date-editor,
  .client-form-dialog .vue-tel-input,
  .client-form-dialog .el-form-item,
  .client-form-dialog .client-field,
  .client-form-dialog .client-form-top-card__content,
  .client-form-dialog .client-form-section__grid,
  .client-form-dialog .client-form-top-grid {
    min-width: 0 !important;
    width: 100%;
  }

  .client-form-dialog .el-button--default,
  .client-form-dialog .el-button--primary {
    min-width: 100px;
  }
}

@media (min-width: 769px) and (max-width: 900px) {
  .client-form-dialog.el-dialog {
    width: calc(100vw - 24px) !important;
  }

  .client-form-top-grid {
    grid-template-columns: 1fr 1fr;
  }

  .client-field--sex {
    grid-column: span 2;
  }

  .client-form-section__grid--three {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .client-form-dialog.el-dialog {
    width: calc(100vw - 10px) !important;
    max-width: calc(100vw - 10px) !important;
    margin-top: 6px !important;
  }

  .client-form-dialog .el-dialog__header {
    padding: 10px 12px 0;
  }

  .client-form-dialog .el-dialog__body {
    padding: 8px 12px 12px;
    max-height: calc(100vh - 90px);
  }

  .client-form-dialog .el-dialog__footer {
    padding: 8px 12px 12px;
  }

  .client-form-summary {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 10px 12px;
  }

  .client-form-top-card {
    grid-template-columns: 1fr;
  }

  .client-form-top-card__avatar {
    padding-top: 0;
  }

  .client-form-top-grid {
    grid-template-columns: 1fr;
  }

  .client-field--sex {
    grid-column: auto;
  }

  .client-form-section__grid--two {
    grid-template-columns: 1fr;
  }

  .client-form-dialog .el-tabs__item {
    font-size: 12px !important;
    padding-right: 12px !important;
  }

  .client-form-dialog .el-button--default,
  .client-form-dialog .el-button--primary {
    min-width: 100px;
  }
}

@media (max-width: 420px) {
  .client-form-dialog .el-dialog__footer {
    flex-wrap: wrap;
  }

  .client-form-dialog .el-button--default,
  .client-form-dialog .el-button--primary {
    flex: 1 1 calc(50% - 6px);
  }
}
</style>
